import { Schema } from "effect";
import { ProjectIdSchema, WorkspaceIdSchema } from "./ids";
import {
  CanonicalIdentifierSchema,
  NonNegativeSafeIntegerSchema,
  PapersFlowBoundaryParseOptions,
} from "./standard";
import { utf8ByteLength } from "./utf8";

export const WORKSPACE_ENTRY_MAX_RELATIVE_PATH_BYTES = 240;
export const WORKSPACE_DIRECTORY_PAGE_MAX_ENTRIES = 200;

const WINDOWS_RESERVED_NAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\..*)?$/iu;
const PORTABLE_FORBIDDEN_CHARACTER = /[<>:"/\\|?*]/u;

export function workspaceEntryNameIsPortable(value: string): boolean {
  if (value.length === 0 || value === "." || value === "..") return false;
  if (value !== value.normalize("NFC")) return false;
  if (value.endsWith(".") || value.endsWith(" ")) return false;
  if (PORTABLE_FORBIDDEN_CHARACTER.test(value) || WINDOWS_RESERVED_NAME.test(value)) return false;
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint === undefined || codePoint === 0 || codePoint < 32 || codePoint === 127) {
      return false;
    }
  }
  return utf8ByteLength(value) <= WORKSPACE_ENTRY_MAX_RELATIVE_PATH_BYTES;
}

export function normalizeWorkspaceEntryNameKey(value: string): string {
  return value.normalize("NFC").toLocaleLowerCase("en-US");
}

export const WorkspaceEntryIdSchema = CanonicalIdentifierSchema.check(Schema.isMaxLength(160)).pipe(
  Schema.brand("WorkspaceEntryId"),
);
export const WorkspaceEntryRevisionIdSchema = CanonicalIdentifierSchema.check(
  Schema.isMaxLength(160),
).pipe(Schema.brand("WorkspaceEntryRevisionId"));
export const WorkspaceEntryCursorSchema = CanonicalIdentifierSchema.check(
  Schema.isMaxLength(2_048),
).pipe(Schema.brand("WorkspaceEntryCursor"));
export const WorkspaceDirectorySnapshotTokenSchema = CanonicalIdentifierSchema.check(
  Schema.isMaxLength(160),
).pipe(Schema.brand("WorkspaceDirectorySnapshotToken"));

export const WorkspaceEntryNameSchema = Schema.String.check(
  Schema.makeFilter((value) => workspaceEntryNameIsPortable(value), {
    expected: "a portable NFC-normalized workspace entry name",
  }),
);

export const WorkspaceEntryKindSchema = Schema.Literals(["file", "directory"]);
export const WorkspaceEntryStatusSchema = Schema.Literals([
  "pending",
  "ready",
  "quarantined",
  "tombstoned",
]);

export const WorkspaceEntrySchema = Schema.Struct({
  id: WorkspaceEntryIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  parentId: Schema.optionalKey(WorkspaceEntryIdSchema),
  name: WorkspaceEntryNameSchema,
  kind: WorkspaceEntryKindSchema,
  status: WorkspaceEntryStatusSchema,
  metadataRevision: NonNegativeSafeIntegerSchema,
  currentRevisionId: Schema.optionalKey(WorkspaceEntryRevisionIdSchema),
  mediaType: Schema.optionalKey(
    Schema.String.check(Schema.isMinLength(3), Schema.isMaxLength(160)),
  ),
  byteSize: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  updatedAt: NonNegativeSafeIntegerSchema,
});

export const WorkspaceEntryRevisionPreconditionSchema = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("must-not-exist") }),
  Schema.Struct({
    kind: Schema.Literal("match"),
    revisionId: WorkspaceEntryRevisionIdSchema,
  }),
]);

export const WorkspaceEntryMetadataPreconditionSchema = Schema.Struct({
  expectedMetadataRevision: NonNegativeSafeIntegerSchema,
});

export const WorkspaceDirectoryPageSchema = Schema.Struct({
  entries: Schema.Array(WorkspaceEntrySchema).check(
    Schema.isMaxLength(WORKSPACE_DIRECTORY_PAGE_MAX_ENTRIES),
  ),
  snapshotToken: WorkspaceDirectorySnapshotTokenSchema,
  nextCursor: Schema.optionalKey(WorkspaceEntryCursorSchema),
});

export const decodeWorkspaceEntryName = Schema.decodeUnknownSync(
  WorkspaceEntryNameSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeWorkspaceEntry = Schema.decodeUnknownSync(
  WorkspaceEntrySchema,
  PapersFlowBoundaryParseOptions,
);

export type WorkspaceEntryId = typeof WorkspaceEntryIdSchema.Type;
export type WorkspaceEntryRevisionId = typeof WorkspaceEntryRevisionIdSchema.Type;
export type WorkspaceEntryName = typeof WorkspaceEntryNameSchema.Type;
export type WorkspaceEntryKind = typeof WorkspaceEntryKindSchema.Type;
export type WorkspaceEntryStatus = typeof WorkspaceEntryStatusSchema.Type;
export type WorkspaceEntry = typeof WorkspaceEntrySchema.Type;
export type WorkspaceEntryEncoded = typeof WorkspaceEntrySchema.Encoded;
export type WorkspaceEntryRevisionPrecondition =
  typeof WorkspaceEntryRevisionPreconditionSchema.Type;
export type WorkspaceDirectoryPage = typeof WorkspaceDirectoryPageSchema.Type;
export type WorkspaceDirectoryPageEncoded = typeof WorkspaceDirectoryPageSchema.Encoded;
